import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Mic, Waves, FileText, ArrowRight, Activity, HeartPulse, Droplets, Sparkles, Orbit, Coins, Briefcase, Heart, ShieldCheck } from "lucide-react";

const healthReports = [
  { icon: Activity, title: "Physical Health Mapping", tag: "Free", text: "Body-system frequencies across octave 5." },
  { icon: HeartPulse, title: "Emotional Frequency", tag: "Member", text: "Emotional resonance across octave 4." },
  { icon: Droplets, title: "Blood Biomarkers", tag: "Member", text: "Biomarker indicators across octave 6." },
];

const astroReports = [
  { icon: Orbit, title: "Planetary", text: "Nine-planet vitality & karmic blueprint." },
  { icon: Coins, title: "Money Nakshatra", text: "Your wealth & abundance frequencies." },
  { icon: Briefcase, title: "Career Nakshatra", text: "Your professional destiny map." },
  { icon: Heart, title: "Relationship Nakshatra", text: "Love, connection & harmony frequencies." },
];

const HERO = "/hero-banner.jpg";
const WAVE = "https://images.unsplash.com/photo-1724185773486-0b39642e607e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHNvdW5kJTIwd2F2ZXxlbnwwfHx8fDE3ODkyNTI5MTV8MA&ixlib=rb-4.1.0&q=85";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const steps = [
  { icon: Mic, title: "Record 30 seconds", text: "Hold a steady vowel tone. Our recorder tracks your voice in real time." },
  { icon: Waves, title: "Frequency analysis", text: "We map the notes your voice hits across octaves 4, 5 and 6." },
  { icon: FileText, title: "Read your report", text: "A clear, deterministic wellness map with text, PDF and audio narration." },
];

const reports = [
  { icon: Activity, title: "Physical Health Mapping", tag: "Free", text: "Body-system frequencies across octave 5." },
  { icon: HeartPulse, title: "Emotional Frequency", tag: "Member", text: "Emotional resonance across octave 4." },
  { icon: Droplets, title: "Blood Biomarkers", tag: "Member", text: "Biomarker indicators across octave 6." },
];

const plans = [
  { name: "Free Scan", price: "₹0", period: "one-time", features: ["One voice scan", "Physical Health Mapping", "Text · PDF · audio"], cta: "Start free", to: "/register", highlight: false },
  { name: "30 Days", price: "₹5,555", period: "30 days", features: ["All health reports", "11-day scan cycle", "Sound prescription"], cta: "Choose plan", to: "/products", highlight: false },
  { name: "60 Days", price: "₹9,999", period: "60 days", features: ["Everything in 30 days", "Inner Genie meditations", "Extended prescriptions"], cta: "Choose plan", to: "/products", highlight: true },
  { name: "90 Days", price: "₹12,999", period: "90 days", features: ["Everything in 60 days", "20-min prescriptions", "Priority support"], cta: "Choose plan", to: "/products", highlight: false },
];

const astroPlans = [
  { icon: Orbit, title: "Planetary", desc: "Full Astro-Planet frequency analysis — nine-planet vitality & karmic blueprint.", price: "₹1,599", regular: "₹3,100", href: "https://rzp.io/l/astrofreqpackage" },
  { icon: Coins, title: "Money Nakshatra", desc: "Targeted wealth, abundance and financial-flow frequency analysis.", price: "₹2,499", regular: "₹5,001", href: "https://rzp.io/l/moneynakshatra" },
  { icon: Briefcase, title: "Career Nakshatra", desc: "Professional growth, direction and career frequency analysis.", price: "₹2,099", regular: "₹4,100", href: "https://rzp.io/l/voicecareer" },
  { icon: Heart, title: "Relationship Nakshatra", desc: "Love, connection & harmony frequencies for partnerships.", price: "₹2,499", regular: "₹5,001", href: "/products" },
];

export default function Home() {
  return (
    <div>
      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="relative overflow-hidden border-b border-border">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-28">
          <div className="max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-primary mb-6">VoiceMatrix AI · Voice-frequency wellness</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">One voice, <span className="text-primary">multiple reports.</span></h1>
            <p className="text-lg text-secondary-foreground/90 max-w-xl mb-10 leading-relaxed">A single 30-second recording unlocks three health reports and four astro reports — rendered live as charts, bars and diagrams with downloadable PDFs.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <motion.div {...fade} className="rounded-2xl border border-border bg-card/80 backdrop-blur p-7">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-2xl">3 Health Reports</h2>
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Wellness</span>
              </div>
              <div className="space-y-3">
                {healthReports.map((r) => (
                  <Link to={r.tag === "Free" ? "/register" : "/products"} key={r.title} className="flex items-start gap-3.5 rounded-xl border border-border bg-background/40 p-4 hover:border-primary/40 transition-colors" data-testid={`home-health-${r.title.toLowerCase().replace(/\s/g, "-")}`}>
                    <span className="grid place-items-center h-10 w-10 rounded-lg bg-secondary text-primary shrink-0"><r.icon strokeWidth={1.5} size={19} /></span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm">{r.title}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.tag === "Free" ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground border border-border"}`}>{r.tag}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{r.text}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }} className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 to-card/80 backdrop-blur p-7">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-2xl">4 Astro Reports</h2>
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-primary"><Sparkles size={13} strokeWidth={1.5} /> Nakshatra Mitra</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {astroReports.map((r) => (
                  <Link to="/products" key={r.title} className="block rounded-xl border border-border bg-background/40 p-4 hover:border-primary/40 transition-colors" data-testid={`home-astro-${r.title.toLowerCase().replace(/\s/g, "-")}`}>
                    <span className="grid place-items-center h-10 w-10 rounded-lg bg-primary/15 text-primary mb-3"><r.icon strokeWidth={1.5} size={19} /></span>
                    <h3 className="font-medium text-sm">{r.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{r.text}</p>
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/register"><Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold h-12 px-7" data-testid="hero-cta-scan">Take your free scan <ArrowRight size={18} strokeWidth={1.5} /></Button></Link>
            <Link to="/products"><Button size="lg" variant="outline" className="rounded-full border-border h-12 px-7" data-testid="hero-cta-products">Explore products</Button></Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════ INTRODUCTION ═══════════════════ */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <motion.div {...fade} className="max-w-2xl mb-16">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Introduction</span>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3">A new lens on your wellbeing.</h2>
        </motion.div>

        <motion.div {...fade} className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed">
          <p>VoiceMatrix AI analyses the frequencies present in your voice and creates a personalised frequency map from your 30-second voice recording.</p>
          <p>The system examines your vocal frequency patterns, identifies normal, weak and missing frequencies, and maps those frequency patterns into two distinct experiences:</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6 mt-10 mb-10 max-w-3xl">
          <motion.div {...fade} transition={{ duration: 0.5 }} className="rounded-2xl border border-border bg-card p-8">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary mb-6"><Activity strokeWidth={1.5} /></span>
            <h3 className="font-serif text-xl mb-2">Health</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">Frequency mapping across selected physical, emotional and body-system indicators.</p>
          </motion.div>
          <motion.div {...fade} transition={{ duration: 0.5, delay: 0.1 }} className="rounded-2xl border border-border bg-card p-8">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary mb-6"><Sparkles strokeWidth={1.5} /></span>
            <h3 className="font-serif text-xl mb-2">Astro</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">Frequency mapping through the 9 planets and 27 Nakshatras of Vedic tradition, generating personalised astrological frequency insights.</p>
          </motion.div>
        </div>

        <motion.div {...fade} className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed">
          <p>Your scan doesn't simply tell you what frequencies are present. It helps identify where frequencies are weak or missing, creating a personalised frequency profile that can be explored through reports, sound-based practices and traditional wellness approaches.</p>
          <p className="text-foreground font-medium">No birth data is required for the voice-frequency scan. No blood sample. No wearable. Just your voice.</p>
        </motion.div>
      </section>

      {/* ═══════════════════ WHY VOICE ANALYSIS ═══════════════════ */}
      <section className="border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <motion.div {...fade} className="max-w-2xl mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Why the voice</span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3">The signal you already carry.</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Waves, title: "Always Available", body: "Your voice is with you everywhere. No equipment, no appointment, no waiting room. A quiet corner and 30 seconds is all it takes." },
              { icon: Activity, title: "Reflects Change", body: "Vocal frequencies shift with stress, sleep, hydration, mood and season. Repeat scans show how you change over time — not just a snapshot." },
              { icon: ShieldCheck, title: "Non-Invasive", body: "Nothing to swallow, no needles, no radiation. The analysis runs on sound alone — the safest signal your body produces." },
            ].map((card, i) => (
              <motion.div key={card.title} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }} className="rounded-2xl border border-border bg-card p-8">
                <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary mb-6"><card.icon strokeWidth={1.5} /></span>
                <h3 className="font-serif text-xl mb-2">{card.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{card.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ HOW IT WORKS ═══════════════════ */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <motion.div {...fade} className="max-w-2xl mb-16">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">How it works</span>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3">Three steps to your frequency map.</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.title} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }} className="rounded-2xl border border-border bg-card p-8">
              <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary mb-6"><s.icon strokeWidth={1.5} /></span>
              <h3 className="font-serif text-xl mb-2">{s.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ═══════════════════ THE REPORTS ═══════════════════ */}
      <section className="border-y border-border bg-muted/30 grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img src={WAVE} alt="Sound frequency visualization" className="rounded-2xl border border-border w-full object-cover aspect-square" />
          </div>
          <div className="md:col-span-7">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">The reports</span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3 mb-8">Insight across every octave.</h2>
            <div className="space-y-4">
              {reports.map((r) => (
                <Link to={r.tag === "Free" ? "/register" : "/products"} key={r.title} className="flex items-start gap-4 rounded-xl border border-border bg-card p-5 hover:border-primary/40 transition-colors">
                  <span className="grid place-items-center h-11 w-11 rounded-lg bg-secondary text-primary shrink-0"><r.icon strokeWidth={1.5} /></span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{r.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.tag === "Free" ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground border border-border"}`}>{r.tag}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{r.text}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ HEALTH MEMBERSHIPS ═══════════════════ */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <motion.div {...fade} className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Health Memberships</span>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3">Start free. Go deeper when you're ready.</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => (
            <div key={p.name} className={`rounded-2xl p-7 flex flex-col ${p.highlight ? "border-2 border-primary bg-primary/5" : "border border-border bg-card"}`} data-testid={`plan-${p.name.toLowerCase().replace(/\s/g, "-")}`}>
              {p.highlight && <span className="text-[10px] uppercase tracking-[0.2em] text-primary mb-2">Most popular</span>}
              <h3 className="font-serif text-xl">{p.name}</h3>
              <div className="my-4"><span className="font-serif text-3xl">{p.price}</span> <span className="text-muted-foreground text-sm">/ {p.period}</span></div>
              <ul className="space-y-2 text-sm text-muted-foreground flex-1 mb-6">
                {p.features.map((f) => <li key={f} className="flex items-center gap-2"><Sparkles size={13} className="text-primary shrink-0" strokeWidth={1.5} /> {f}</li>)}
              </ul>
              <Link to={p.to}><Button className={`w-full rounded-full font-semibold ${p.highlight ? "bg-primary text-primary-foreground hover:bg-primary/85" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`}>{p.cta}</Button></Link>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════ ASTRO REPORTS — HYBRID ═══════════════════ */}
      <section className="border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <motion.div {...fade} className="max-w-2xl mb-16">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary"><Sparkles size={13} strokeWidth={1.5} /> Nakshatra Mitra · Astro Reports</span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3">Buy astro reports individually.</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {astroPlans.map((r, i) => (
              <motion.div key={r.title} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }} className="rounded-2xl border border-border bg-card p-7 flex flex-col">
                <span className="grid place-items-center h-12 w-12 rounded-full bg-primary/15 text-primary mb-6"><r.icon strokeWidth={1.5} /></span>
                <h3 className="font-serif text-xl mb-2">{r.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed flex-1 mb-6">{r.desc}</p>
                <div className="mb-5">
                  <span className="font-serif text-3xl">{r.price}</span>
                  <span className="text-muted-foreground text-sm line-through ml-2">{r.regular}</span>
                </div>
                <a href={r.href} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold">Buy report <ArrowRight size={16} strokeWidth={1.5} /></Button>
                </a>
              </motion.div>
            ))}
          </div>

          <motion.div {...fade} className="text-center">
            <Link to="/products">
              <Button variant="outline" className="rounded-full border-border">
                View all astro & health products <ArrowRight size={16} strokeWidth={1.5} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════ CLOSING CTA ═══════════════════ */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="rounded-3xl border border-primary/20 bg-primary/5 p-10 sm:p-16 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-4">Hear what your voice has to say.</h2>
          <p className="text-muted-foreground max-w-lg mx-auto mb-8">Your first scan and Physical Health Mapping are free. No card required.</p>
          <Link to="/register"><Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold h-12 px-8" data-testid="footer-cta-scan">Start your free scan</Button></Link>
        </div>
      </section>
    </div>
  );
}